// landing: src/App.jsx
//
// Brooke Refrigeration - landing page.
//
// A single interactive object: a double-door commercial cabinet. The doors
// swing open to reveal the three divisions on lit shelves, and can be closed
// and reopened by clicking. Built as a real 3D scene with preserve-3d, so the
// cabinet reads as a cavity rather than a flat panel.
//
// Every link goes somewhere real: the three divisions, Call us, About and
// Contact. A link marked live:false would open the "coming soon" modal
// instead, so nothing ever points at a page that does not exist yet.
// The footer carries Support and Privacy, which the app stores link to.
//
// Fonts and meta live in public/index.html rather than being injected here,
// so a crawler sees them without executing JavaScript.
//
// Sizing note: the cabinet dimensions are driven by --bk-unit-h / --bk-unit-w
// on .bk-page, so the mobile breakpoint can shrink the whole object.
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
} from "motion/react";

import Content from "./Content";

// ---------------------------------------------------------------------------
// Brand + content
// ---------------------------------------------------------------------------
const C = {
  red: "#E7001A",
  navy: "#304E96",
  steel: "#8FA3B0",
  steelLt: "#D7E1E8",
  frost: "#EEF6FA",
  led: "#8FE3F5",
  mute: "#6D8494",
  ink: "#12232F",
  inkSoft: "#4A6478",
};

// Set live:true and the shelf becomes a real link. Until then it opens the
// modal instead, so nothing points at a dead subdomain.
const DIVISIONS = [
    {
    n: "01",
    title: "Maintenance",
    copy: "Service contracts, callouts and scheduled maintenance.",
    href: "https://maintenance.brookerefrigeration.com",
    live: true,
  },
  {
    n: "02",
    title: "Cold Storage",
    copy: "Cold room design, installation and plant work.",
    href: "https://coldstorage.brookerefrigeration.com",
    live: true,
  },
  {
    n: "03",
    title: "Requip",
    copy: "Refurbished commercial equipment, ready to run.",
    href: "https://requip.brookerefrigeration.com",
    live: true,
  },
];

const CRISPER = [
  { label: "About Brooke", meta: "Since 2000", href: "/about/", live: true },
  // The support page carries the office email
  { label: "Contact", meta: "Email the office", href: "/support/", live: true },
];

// The office number, for Call us. The same one the division sites show.
const PHONE = { label: "078 220 5037", href: "tel:+27782205037" };

// What the modal says, depending on what was clicked.
const modalForDivision = (d) => ({
  kicker: `Division ${d.n}`,
  title: d.title,
  copy: "This one is still on ice. We are building it out now, so check back shortly.",
});

const modalForCrisper = (c) => ({
  kicker: "Coming soon",
  title: c.label,
  copy: "This page is still being built. It lands with the rest of the new site.",
});

const TEMP_SEQ = [-18, -16, -14, -15, -17, -18];
const FLAKE_COUNT = 60;
// Phones get roughly a third of the flakes - see Frost()
const FLAKE_COUNT_SM = 22;

// Six-armed snowflakes drawn as SVG data URIs. Three designs so the fall does
// not look tiled: a full branched crystal, a simpler one that stays legible at
// small sizes, and a hexagonal plate.
const FLAKE_SVG = [
  "%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20viewBox%3D%270%200%2024%2024%27%20fill%3D%27none%27%20stroke%3D%27white%27%20stroke-width%3D%271.5%27%20stroke-linecap%3D%27round%27%20stroke-linejoin%3D%27round%27%3E%3Cg%3E%3Cpath%20d%3D%27M12%201.5V22.5%27%2F%3E%3Cpath%20d%3D%27M2.9%206.75%2021.1%2017.25%27%2F%3E%3Cpath%20d%3D%27M2.9%2017.25%2021.1%206.75%27%2F%3E%3Cpath%20d%3D%27M12%205.2%209.4%203.1M12%205.2%2014.6%203.1M12%2018.8%209.4%2020.9M12%2018.8%2014.6%2020.9%27%2F%3E%3Cpath%20d%3D%27M6.1%208.6%205.7%205.3M6.1%208.6%203.1%209.5M17.9%2015.4%2018.3%2018.7M17.9%2015.4%2020.9%2014.5%27%2F%3E%3Cpath%20d%3D%27M6.1%2015.4%203.1%2014.5M6.1%2015.4%205.7%2018.7M17.9%208.6%2020.9%209.5M17.9%208.6%2018.3%205.3%27%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E",
  "%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20viewBox%3D%270%200%2024%2024%27%20fill%3D%27none%27%20stroke%3D%27white%27%20stroke-width%3D%271.8%27%20stroke-linecap%3D%27round%27%20stroke-linejoin%3D%27round%27%3E%3Cg%3E%3Cpath%20d%3D%27M12%202V22%27%2F%3E%3Cpath%20d%3D%27M3.3%207%2020.7%2017%27%2F%3E%3Cpath%20d%3D%27M3.3%2017%2020.7%207%27%2F%3E%3Cpath%20d%3D%27M12%206.4%209.8%204.6M12%206.4%2014.2%204.6M12%2017.6%209.8%2019.4M12%2017.6%2014.2%2019.4%27%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E",
  "%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20viewBox%3D%270%200%2024%2024%27%20fill%3D%27none%27%20stroke%3D%27white%27%20stroke-width%3D%271.3%27%20stroke-linecap%3D%27round%27%20stroke-linejoin%3D%27round%27%3E%3Cg%3E%3Cpath%20d%3D%27M12%202.6%2019.1%206.8V15.2L12%2019.4%204.9%2015.2V6.8Z%27%2F%3E%3Cpath%20d%3D%27M12%206.2V15.8M8.2%208.4%2015.8%2013.6M8.2%2013.6%2015.8%208.4%27%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E",
];

// Heavy and damped - a commercial door has mass, so it settles rather than
// bouncing back. The right door lags fractionally so they do not move in
// lockstep, which is what makes the pair feel physical.
const doorSpring = (delay = 0) => ({
  type: "spring",
  stiffness: 42,
  damping: 17,
  mass: 1.5,
  delay,
});

// ---------------------------------------------------------------------------
export default function App() {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [temp, setTemp] = useState(-18);
  const [touch, setTouch] = useState(false);
  const [modal, setModal] = useState(null);
  const stageRef = useRef(null);
  const autoRef = useRef(null);

  // Touch devices get no parallax - it has nothing to track
  useEffect(() => {
    setTouch(window.matchMedia("(hover: none)").matches);
  }, []);

  // --- the doors open on their own, so they are theatre rather than a gate -
  useEffect(() => {
    if (reduce) {
      setOpen(true);
      return;
    }
    // Long enough for the closed unit to register as an object before it moves
    autoRef.current = setTimeout(() => setOpen(true), 1900);
    return () => clearTimeout(autoRef.current);
  }, [reduce]);

  // --- temperature drifts as cold air escapes and the unit recovers -------
  useEffect(() => {
    if (reduce) return;
    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % TEMP_SEQ.length;
      setTemp(TEMP_SEQ[i]);
    }, 2600);
    return () => clearInterval(t);
  }, [reduce]);

  // Clicking anywhere on the unit toggles. Cancels the auto-open first, so an
  // early click is not undone a moment later.
  const toggle = useCallback(() => {
    clearTimeout(autoRef.current);
    setHovered(null);
    setOpen((v) => !v);
  }, []);

  const closeModal = useCallback(() => setModal(null), []);

  // --- cursor parallax: the unit turns slightly toward the pointer --------
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const tiltY = useTransform(sx, [-1, 1], [7, -7]);
  const tiltX = useTransform(sy, [-1, 1], [-5, 5]);

  const onMove = useCallback(
    (e) => {
      if (reduce || touch || !stageRef.current) return;
      const r = stageRef.current.getBoundingClientRect();
      mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
      my.set(((e.clientY - r.top) / r.height) * 2 - 1);
    },
    [mx, my, reduce, touch]
  );

  const onLeave = useCallback(() => {
    mx.set(0);
    my.set(0);
  }, [mx, my]);

  return (
    <div className="bk-page" style={S.page}>
      <Styles />
      <Frost reduce={reduce} open={open} />

      {/* ---------------- header ---------------- */}
      <header style={S.top}>
        <div style={S.mark}>
          <b style={S.markB}>BROOKE</b>
          <span style={S.markS}>Refrigeration</span>
        </div>
        <a className="bk-call" style={{ ...S.call, textDecoration: "none" }} href={PHONE.href}>
          Call us
        </a>
      </header>

      {/* ---------------- stage ---------------- */}
      <main
        ref={stageRef}
        style={S.stage}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        <motion.div
          style={{ ...S.unit, rotateX: tiltX, rotateY: tiltY }}
          initial={{ opacity: 0, y: 40, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* floor shadow spreads as the doors swing wide */}
          <motion.div
            style={S.floorShadow}
            animate={{ opacity: open ? 0.5 : 0.75, scaleX: open ? 1.2 : 1 }}
            transition={{ duration: 1.6 }}
          />

          {/* ---------- cabinet interior ---------- */}
          <div style={S.cabinet}>
            <div style={{ ...S.wall, ...S.wallL }} />
            <div style={{ ...S.wall, ...S.wallR }} />
            <div style={S.backWall} />

            <motion.div
              style={S.wash}
              animate={{ opacity: open ? 1 : 0 }}
              transition={{ duration: 1.1, delay: open ? 0.35 : 0 }}
            />

            <motion.div
              style={S.led}
              animate={{ opacity: open ? 1 : 0, scaleX: open ? 1 : 0.4 }}
              transition={{ duration: 0.5, delay: open ? 0.3 : 0 }}
            />

            <div style={S.cabInner}>
              <motion.div
                style={S.cabHead}
                initial={false}
                animate={{ opacity: open ? 1 : 0, y: open ? 0 : -8 }}
                transition={{ duration: 0.5, delay: open ? 0.65 : 0 }}
              >
                <p style={S.cabHeadP}>Select a division</p>
                <span style={S.cabHeadS}>
                  <i className="bk-dot" style={S.dot} />
                  {DIVISIONS.length} units
                </span>
              </motion.div>

              <div style={S.shelves}>
                {DIVISIONS.map((d, i) => (
                  <Shelf
                    key={d.n}
                    d={d}
                    i={i}
                    open={open}
                    reduce={reduce}
                    hovered={hovered}
                    setHovered={setHovered}
                    onBlocked={() => setModal(modalForDivision(d))}
                  />
                ))}
              </div>

              {/* crisper drawer - About and Contact live in the base */}
              <Crisper
                open={open}
                reduce={reduce}
                onBlocked={(c) => setModal(modalForCrisper(c))}
              />
            </div>
          </div>

          {/* ---------- cold air spilling over the lip ---------- */}
          <motion.div
            style={S.fog}
            animate={{ opacity: open ? 0.8 : 0 }}
            transition={{ duration: 1.4, delay: open ? 0.8 : 0 }}
          >
            <i className="bk-fog bk-fog1" />
            <i className="bk-fog bk-fog2" />
            <i className="bk-fog bk-fog3" />
          </motion.div>

          {/* ---------- doors ---------- */}
          <Door
            side="left"
            open={open}
            reduce={reduce}
            onToggle={toggle}
            temp={temp}
          />
          <Door
            side="right"
            open={open}
            reduce={reduce}
            onToggle={toggle}
            temp={temp}
          />

          {/* hinges on the outer edges */}
          <div style={{ ...S.hinge, ...S.hingeL, top: "15%" }} />
          <div style={{ ...S.hinge, ...S.hingeL, bottom: "15%" }} />
          <div style={{ ...S.hinge, ...S.hingeR, top: "15%" }} />
          <div style={{ ...S.hinge, ...S.hingeR, bottom: "15%" }} />

          {/* cold light escaping the seal while the doors are shut */}
          <motion.div
            style={S.seal}
            animate={{ opacity: open ? 0 : 1 }}
            transition={{ duration: 0.6 }}
          />
        </motion.div>

        {/* Sits directly under the cabinet. Once open the doors are edge-on
            and barely tappable, so this is the real control on a phone. */}
        <AnimatePresence mode="wait">
          <motion.button
            key={open ? "open" : "shut"}
            onClick={toggle}
            style={S.prompt}
            className="bk-prompt"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.5, delay: open ? 1.3 : 0.2 }}
          >
            <i style={S.promptI} />
            {open ? "Close the doors" : "Open the doors"}
            <i style={S.promptI} />
          </motion.button>
               </AnimatePresence>

        <motion.div
          style={S.scrollCue}
          initial={{ opacity: 0 }}
          animate={{ opacity: open ? 1 : 0 }}
          transition={{ duration: 0.6, delay: open ? 1.8 : 0 }}
          aria-hidden="true"
        >
          
          <span className="bk-cue" style={S.scrollCueLine} />
        </motion.div>
      </main>

      <Content />

      <Modal item={modal} onClose={closeModal} />

      {/* ---------------- footer ---------------- */}
      <footer style={S.foot}>
        <div>Brooke Refrigeration &middot; Founded 2000 by Teddy and Ellie</div>
        <div style={S.footSep}>
                   {DIVISIONS.map((d) =>
            d.live ? (
              <a key={d.n} className="bk-foota" style={S.footA} href={d.href}>
                {d.title}
              </a>
            ) : (
              <button
                key={d.n}
                type="button"
                className="bk-foota"
                style={S.footA}
                onClick={() => setModal(modalForDivision(d))}
              >
                {d.title}
              </button>
            )
          )}
        </div>
        {/* The app stores link here, so they sit on every visit's way out */}
        <div style={S.footSep}>
          <a className="bk-foota" style={S.footA} href="/support/">
            Support
          </a>
          <a className="bk-foota" style={S.footA} href="/privacy/">
            Privacy
          </a>
        </div>
        <div style={S.footSep}>
          <span style={S.footA}>
            Developed by{" "}
            <a className="bk-foota" style={S.footA} href="https://codebooth.co.za" target="_blank" rel="noopener">
              CodeBooth
            </a>
          </span>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One door. Hinged on its outer edge, so the pair opens from the centre seam.
// ---------------------------------------------------------------------------
function Door({ side, open, reduce, onToggle, temp }) {
  const left = side === "left";
  const angle = left ? -112 : 112;

  return (
    <motion.div
      onClick={onToggle}
      style={{
        ...S.door,
        ...(left ? S.doorL : S.doorR),
        cursor: "pointer",
      }}
      animate={{ rotateY: open ? angle : 0 }}
      transition={reduce ? { duration: 0 } : doorSpring(left ? 0 : 0.06)}
    >
      {/* outer face - brushed steel */}
      <div style={{ ...S.face, ...S.faceOut, ...(left ? S.faceOutL : S.faceOutR) }}>
        <div style={S.grain} />
        <div className="bk-sheen" style={S.sheen} />

        {left ? (
          <>
            <div style={S.brand}>
              <h1 style={S.brandH}>
                Brooke
                <em style={S.brandEm}>Refrigeration</em>
              </h1>
            </div>
            <div style={S.est}>Est. 2000</div>
          </>
        ) : (
          <>
            <div style={S.certWrap}>
              <div style={S.cert}>Commercial</div>
              <div style={S.cert}>Refrigeration</div>
            </div>

            <div style={S.panel}>
              <div style={S.temp}>
                &minus;{Math.abs(temp)}
                <small style={S.tempS}>&deg;C</small>
              </div>
              <div style={S.status}>
                <p style={S.statusP}>
                  <i className="bk-dot" style={S.dot} />
                  System OK
                </p>
                <p style={S.statusP}>25 yrs &middot; 850+ clients</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* inner face - liner and door bins */}
      <div style={{ ...S.face, ...S.faceIn }}>
        <div style={S.gasket} />
        <div style={S.bin} />
        <div style={S.bin} />
        <div style={S.bin} />
      </div>

      {/* handle sits at the centre seam, where the pair meets */}
      <div style={{ ...S.handle, ...(left ? S.handleL : S.handleR) }}>
        <span style={{ ...S.handleTab, ...(left ? S.tabL : S.tabR), top: 9 }} />
        <span style={{ ...S.handleTab, ...(left ? S.tabL : S.tabR), bottom: 9 }} />
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// A shelf. Hovering pulls it toward the viewer on z, the way a rack slides
// out, and dims the others so the focus is unambiguous.
//
// Rendered as an anchor with a real href so the destination is visible on
// hover and crawlable, but the click is intercepted until the site is live.
// ---------------------------------------------------------------------------
function Shelf({ d, i, open, reduce, hovered, setHovered, onBlocked }) {
  const on = hovered === i;
  const dim = hovered !== null && !on;

  const click = (e) => {
    e.stopPropagation();
    if (!d.live) {
      e.preventDefault();
      onBlocked();
    }
  };

  return (
    <motion.a
      href={d.href}
      style={S.shelf}
      className="bk-shelf"
      initial={false}
      // links must not toggle the doors underneath them
      onClick={click}
      animate={{
        opacity: open ? (dim ? 0.55 : 1) : 0,
        y: open ? 0 : 18,
        z: on && !reduce ? 34 : 0,
        scale: on && !reduce ? 1.02 : 1,
      }}
      transition={{
        opacity: { duration: 0.55, delay: open ? 0.75 + i * 0.12 : 0 },
        y: {
          duration: 0.55,
          delay: open ? 0.75 + i * 0.12 : 0,
          ease: [0.22, 1, 0.36, 1],
        },
        z: { type: "spring", stiffness: 260, damping: 22 },
        scale: { type: "spring", stiffness: 260, damping: 22 },
      }}
      onMouseEnter={() => setHovered(i)}
      onMouseLeave={() => setHovered(null)}
      onFocus={() => setHovered(i)}
      onBlur={() => setHovered(null)}
    >
      <span style={S.rack} />
      <span style={{ ...S.tag, color: on ? C.red : C.steel }}>{d.n}</span>
      {!d.live && <span style={S.frosted}>On ice</span>}

      <span style={S.shelfBody}>
        <h2 style={S.shelfH}>{d.title}</h2>
        <p style={S.shelfP}>{d.copy}</p>
      </span>

      <motion.span
        style={{ ...S.go, color: on ? C.red : C.steel }}
        animate={{ x: on ? 6 : 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 24 }}
      >
        &rarr;
      </motion.span>
    </motion.a>
  );
}

// ---------------------------------------------------------------------------
// Crisper drawer at the base. About and Contact sit behind a real drawer front
// with a recessed grip, and the whole thing slides toward the viewer on hover.
// ---------------------------------------------------------------------------
function Crisper({ open, reduce, onBlocked }) {
  const [out, setOut] = useState(false);

  return (
    <motion.div
      style={S.crisper}
      initial={false}
      animate={{
        opacity: open ? 1 : 0,
        y: open ? 0 : 16,
        z: out && !reduce ? 26 : 0,
      }}
      transition={{
        opacity: { duration: 0.6, delay: open ? 1.15 : 0 },
        y: { duration: 0.6, delay: open ? 1.15 : 0, ease: [0.22, 1, 0.36, 1] },
        z: { type: "spring", stiffness: 240, damping: 24 },
      }}
      onMouseEnter={() => setOut(true)}
      onMouseLeave={() => setOut(false)}
      onClick={(e) => e.stopPropagation()}
    >
      {/* recessed grip along the top edge of the drawer front */}
      <div style={S.grip} />

      <div style={S.crisperRow}>
        {CRISPER.map((c) => (
          <a
            key={c.label}
            href={c.href}
            className="bk-crisp"
            style={S.crispItem}
            onClick={(e) => {
              e.stopPropagation();
              if (!c.live) {
                e.preventDefault();
                onBlocked(c);
              }
            }}
          >
            <span style={S.crispLabel}>{c.label}</span>
            <span className="bk-crisp-meta" style={S.crispMeta}>{c.meta}</span>
            <span className="bk-crisp-go" style={S.crispGo}>
              &rarr;
            </span>
          </a>
        ))}
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Coming-soon modal. One card for every blocked destination on the page.
//
// Deliberately quiet: the cabinet is the signature element, so this sits back
// as a plain rounded card rather than competing with it.
// ---------------------------------------------------------------------------
function Modal({ item, onClose }) {
  const closeRef = useRef(null);

  // Escape closes, the body is locked while it is up, and focus moves to the
  // dismiss button so a keyboard user is not left behind the overlay.
  useEffect(() => {
    if (!item) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => closeRef.current?.focus(), 60);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [item, onClose]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          style={S.mWrap}
          className="bk-m-wrap"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="bk-m-title"
        >
          <motion.div
            style={S.mCard}
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 320, damping: 30, mass: 0.8 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              className="bk-m-x"
              style={S.mX}
              aria-label="Close"
            >
              &times;
            </button>

            <div style={S.mKicker}>{item.kicker}</div>
            <h3 id="bk-m-title" style={S.mTitle}>{item.title}</h3>
            <p style={S.mCopy}>{item.copy}</p>

            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              className="bk-m-cta"
              style={S.mCta}
            >
              Back to the shelf
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// Falling frost. Positions are generated once so they do not reshuffle on
// every render, and the whole layer intensifies while the doors are open.
// ---------------------------------------------------------------------------
function Frost({ reduce, open }) {
  const [count, setCount] = useState(FLAKE_COUNT);

  // Fewer flakes on a phone. Each flake is its own composited layer, so 60 of
  // them is what makes the page stutter on mobile.
  useEffect(() => {
    const m = window.matchMedia("(max-width:560px)");
    const apply = () => setCount(m.matches ? FLAKE_COUNT_SM : FLAKE_COUNT);
    apply();
    m.addEventListener("change", apply);
    return () => m.removeEventListener("change", apply);
  }, []);

  const flakes = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => {
        // A third sit in front of the cabinet and are larger and faster, which
        // is what creates depth rather than a flat curtain of dots.
        const front = i % 3 === 0;
        // Small flakes get the simpler design, which stays legible; the plate
        // is used sparingly so the mix does not read as one repeated shape.
        const size = front ? 13 + Math.random() * 13 : 6 + Math.random() * 8;
        const shape = size > 15 ? (i % 7 === 0 ? 2 : 0) : 1;
        return {
          id: i,
          left: Math.random() * 106 - 3,
          size,
          shape,
          dur: front ? 9 + Math.random() * 8 : 14 + Math.random() * 16,
          delay: -Math.random() * 26,
          drift: -80 + Math.random() * 160,
          spin: Math.random() < 0.5 ? 260 : -260,
          op: front ? 0.55 + Math.random() * 0.4 : 0.22 + Math.random() * 0.4,
          blur: front ? 0 : Math.random() < 0.45 ? 1 : 0,
          front,
        };
      }),
    [count]
  );

  if (reduce) return null;

  return (
    <motion.div
      style={S.frost}
      animate={{ opacity: open ? 1 : 0.55 }}
      transition={{ duration: 1.6 }}
      aria-hidden="true"
    >
      {/* frost creeping in from the edges of the frame */}
      <div style={S.vignette} />
      {flakes.map((f) => (
        <span
          key={f.id}
          // the glow and softening live in CSS so the mobile query can strip
          // them - inline filters cannot be overridden by a media rule
          className={`bk-flake${f.blur ? " bk-flake-soft" : ""}`}
          style={{
            left: `${f.left}%`,
            width: f.size,
            height: f.size,
            opacity: f.op,
            backgroundImage: `url("data:image/svg+xml,${FLAKE_SVG[f.shape]}")`,
            animationDuration: `${f.dur}s`,
            animationDelay: `${f.delay}s`,
            zIndex: f.front ? 40 : 1,
            "--drift": `${f.drift}px`,
            "--spin": `${f.spin}deg`,
          }}
        />
      ))}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Keyframes and hover states that inline styles cannot express
// ---------------------------------------------------------------------------
const Styles = () => (
  <style>{`
    /* ---- sizing vars: one place to resize the whole object ---- */
    .bk-page{
      --bk-unit-h: min(66svh, 620px);
      --bk-unit-w: min(calc(66svh * .78), 94vw, 520px);
      --bk-handle-w: 13px;
      --bk-hinge-w: 11px;
      --bk-hinge-h: 34px;
    }

    @keyframes bkSheen {
      0%,62%   { transform: translateX(-120%) skewX(-14deg); }
      88%,100% { transform: translateX(350%)  skewX(-14deg); }
    }
    @keyframes bkPulse { 0%,100%{opacity:1} 50%{opacity:.32} }
    @keyframes bkRoll {
      0%,100%{transform:translate(0,14%) scale(1);opacity:.5}
      50%{transform:translate(7%,4%) scale(1.14);opacity:.85}
    }
    @keyframes bkFall {
      0%   { transform:translate(0,-12vh) rotate(0deg); }
      50%  { transform:translate(calc(var(--drift,0px) * .55),49vh)
                       rotate(calc(var(--spin,300deg) * .5)); }
      100% { transform:translate(var(--drift,0px),112vh) rotate(var(--spin,300deg)); }
    }

        @keyframes bkCue {
      0%,100% { transform:translateY(0); opacity:.55 }
      50%     { transform:translateY(5px); opacity:1 }
    }

    .bk-sheen{ animation: bkSheen 7.5s ease-in-out infinite; }
    .bk-dot{ animation: bkPulse 2.4s ease-in-out infinite; }
    .bk-cue{ animation: bkCue 2.2s ease-in-out infinite; }

    .bk-flake{
      position:absolute; top:0; display:block;
      background-repeat:no-repeat; background-position:center;
      background-size:contain;
      animation-name:bkFall; animation-timing-function:linear;
      animation-iteration-count:infinite;
    }

    /* Glow and softening are desktop only. Both are per-flake filter layers,
       and a filtered element re-rasterises on every frame it moves. */
    @media (min-width:561px){
      .bk-flake{ filter:drop-shadow(0 0 4px rgba(143,227,245,.75)); }
      .bk-flake-soft{ filter:drop-shadow(0 0 4px rgba(143,227,245,.75)) blur(1px); }
    }

    .bk-fog{
      position:absolute; bottom:0; display:block;
      width:52%; height:100%; border-radius:50%;
      background:radial-gradient(ellipse at 50% 70%, rgba(214,238,246,.5), transparent 68%);
      filter:blur(20px); animation: bkRoll 13s ease-in-out infinite;
    }
    .bk-fog1{ left:-2%; animation-delay:0s }
    .bk-fog2{ left:26%; height:82%; animation-delay:-4.5s }
    .bk-fog3{ left:54%; height:92%; animation-delay:-9s }

    .bk-shelf{ text-decoration:none; color:inherit; }
    .bk-shelf:hover, .bk-shelf:focus-visible{
      background:linear-gradient(180deg, rgba(231,0,26,.15), rgba(231,0,26,.03)) !important;
      border-color:rgba(231,0,26,.55) !important;
      outline:none;
    }
    .bk-shelf:focus-visible{ box-shadow:0 0 0 2px ${C.led}; }

    .bk-crisp{ text-decoration:none; }
    .bk-crisp:hover, .bk-crisp:focus-visible{
      background:rgba(231,0,26,.16) !important; outline:none;
    }
    .bk-crisp:hover .bk-crisp-go, .bk-crisp:focus-visible .bk-crisp-go{
      color:${C.red}; transform:translateX(4px);
    }

    .bk-prompt{ cursor:pointer; transition:border-color .25s, background .25s, color .25s; }
    .bk-prompt:hover, .bk-prompt:focus-visible{
      border-color:${C.red} !important;
      background:rgba(231,0,26,.16) !important;
      color:#fff !important;
      outline:none;
    }

    .bk-call{ cursor:pointer; }
    .bk-call:hover, .bk-call:focus-visible{
      border-color:${C.red} !important; color:#fff !important;
      background:rgba(231,0,26,.14); outline:none;
    }

    .bk-foota{ cursor:pointer; }
    .bk-foota:hover, .bk-foota:focus-visible{
      border-bottom-color:${C.red} !important; outline:none;
    }

    /* ---- modal ---- */
    .bk-m-cta{ cursor:pointer; transition:background .2s, box-shadow .2s; }
    .bk-m-cta:hover{ background:#C40016 !important; }
    .bk-m-cta:focus-visible{
      outline:none; box-shadow:0 0 0 3px rgba(231,0,26,.32);
    }

    .bk-m-x{ cursor:pointer; transition:color .2s, background .2s; }
    .bk-m-x:hover, .bk-m-x:focus-visible{
      color:${C.ink} !important; background:rgba(18,35,47,.06) !important;
      outline:none;
    }

    @media (prefers-reduced-motion: reduce){
            .bk-sheen, .bk-dot, .bk-fog, .bk-flake, .bk-cue{ animation:none !important; }
    }

    /* ---- mobile ---- */
    @media (max-width:560px){
      .bk-shelf p{ display:none; }
      .bk-crisp-meta{ display:none; }

      /* smaller cabinet, and hardware scaled with it so the handles and
         hinges do not read as chunky against a shorter door */
      .bk-page{
        --bk-unit-h: min(56svh, 480px);
        --bk-unit-w: min(calc(56svh * .78), 86vw, 400px);
        --bk-handle-w: 10px;
        --bk-hinge-w: 9px;
        --bk-hinge-h: 26px;
      }

      /* two fog blobs instead of three, and a cheaper blur radius */
      .bk-fog{ filter:blur(12px); }
      .bk-fog3{ display:none; }

      /* backdrop-filter is the single most expensive thing on mobile Safari */
      .bk-prompt{
        backdrop-filter:none !important;
        -webkit-backdrop-filter:none !important;
        background:rgba(11,22,34,.92) !important;
      }
      .bk-m-wrap{
        backdrop-filter:none !important;
        -webkit-backdrop-filter:none !important;
        background:rgba(6,14,22,.88) !important;
      }
    }
  `}</style>
);

// ---------------------------------------------------------------------------
// Styles. Inline so the page is fully isolated from the surrounding app.
// ---------------------------------------------------------------------------
const mono = "'JetBrains Mono', ui-monospace, monospace";
const arch = "'Archivo', system-ui, sans-serif";

const S = {
  page: {
    fontFamily: "'Inter', system-ui, sans-serif",
    background: `radial-gradient(120% 80% at 50% -10%, #1B2E3F 0%, transparent 60%),
                 linear-gradient(180deg, #0B1622 0%, #101E2B 55%, #0B1622 100%)`,
    color: C.frost,
    minHeight: "100vh",
    overflowX: "hidden",
    position: "relative",
  },

  frost: {
    position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden",
  },
  // cold creeping in from the edges of the frame
  vignette: {
    position: "absolute", inset: 0, pointerEvents: "none",
    background: `radial-gradient(120% 90% at 50% 50%, transparent 52%, rgba(143,227,245,.12) 84%, rgba(143,227,245,.22) 100%)`,
  },

  top: {
    position: "fixed", top: 0, left: 0, right: 0, zIndex: 60,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "20px clamp(16px,4vw,44px)",
    background: "linear-gradient(180deg, rgba(11,22,34,.92), transparent)",
  },
  mark: { display: "flex", alignItems: "baseline", gap: 9 },
  markB: { fontFamily: arch, fontWeight: 800, fontSize: 20, letterSpacing: ".02em", color: "#fff" },
  markS: { fontFamily: mono, fontSize: 10, letterSpacing: ".22em", textTransform: "uppercase", color: C.steel },
  call: {
    fontFamily: mono, fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase",
    color: C.steelLt, background: "transparent",
    border: "1px solid rgba(143,163,176,.32)", borderRadius: 2,
    padding: "9px 14px", transition: ".25s",
  },

  stage: {
    minHeight: "100svh",
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    gap: "clamp(26px,4vh,44px)",
    padding: "92px clamp(14px,4vw,40px) 64px",
    perspective: 2200, position: "relative", zIndex: 5,
  },

  unit: {
    position: "relative", flex: "0 0 auto",
    // see .bk-page in <Styles /> - the mobile breakpoint overrides these
    height: "var(--bk-unit-h)",
    width: "var(--bk-unit-w)",
    transformStyle: "preserve-3d",
  },
  floorShadow: {
    position: "absolute", left: "6%", right: "6%", bottom: "-5%", height: "12%",
    background: "radial-gradient(ellipse at 50% 40%, rgba(0,0,0,.62), transparent 70%)",
    filter: "blur(14px)", zIndex: -2,
  },

  cabinet: {
    position: "absolute", inset: 0, borderRadius: 16,
    background: "linear-gradient(180deg, #16242F 0%, #0D1A24 100%)",
    boxShadow: `inset 0 0 0 2px #2A3D4B,
                inset 0 30px 70px rgba(0,0,0,.85),
                inset 0 -18px 44px rgba(0,0,0,.6)`,
    overflow: "hidden",
    transformStyle: "preserve-3d",
  },
  wall: { position: "absolute", top: 0, bottom: 0, width: "12%", pointerEvents: "none" },
  wallL: { left: 0, background: "linear-gradient(90deg, rgba(0,0,0,.55), transparent)" },
  wallR: { right: 0, background: "linear-gradient(270deg, rgba(0,0,0,.55), transparent)" },
  backWall: {
    position: "absolute", inset: 0, pointerEvents: "none",
    background: "radial-gradient(70% 55% at 50% 42%, rgba(143,227,245,.05), transparent 75%)",
  },
  wash: {
    position: "absolute", inset: 0, pointerEvents: "none",
    background: `linear-gradient(180deg, rgba(143,227,245,.30) 0%, transparent 38%),
                 radial-gradient(85% 45% at 50% 0%, rgba(143,227,245,.26), transparent 72%)`,
  },
  led: {
    position: "absolute", top: 0, left: "10%", right: "10%", height: 3,
    borderRadius: "0 0 4px 4px", background: C.led,
    boxShadow: "0 0 22px 5px rgba(143,227,245,.55)", transformOrigin: "center",
  },
  cabInner: {
    position: "relative", zIndex: 2, height: "100%",
    display: "flex", flexDirection: "column",
    padding: "clamp(14px,3vw,26px)", gap: "clamp(13px,2.6vw,24px)",
  },

  cabHead: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    paddingBottom: "clamp(8px,1.6vw,12px)",
    borderBottom: "1px solid rgba(143,163,176,.2)",
  },
  cabHeadP: { fontFamily: mono, fontSize: "clamp(8px,1.4vw,10px)", letterSpacing: ".22em", textTransform: "uppercase", color: C.steel },
  cabHeadS: { fontFamily: mono, fontSize: "clamp(8px,1.4vw,10px)", color: C.led, letterSpacing: ".1em", display: "inline-flex", alignItems: "center" },

  shelves: {
    display: "flex", flexDirection: "column",
    gap: "clamp(13px,2.6vw,24px)", transformStyle: "preserve-3d",
  },

  shelf: {
    position: "relative", display: "flex", alignItems: "center",
    gap: "clamp(10px,2.2vw,20px)",
    padding: "clamp(13px,2.6vw,22px) clamp(11px,2.2vw,18px)",
    borderRadius: 5,
    background: "linear-gradient(180deg, rgba(255,255,255,.055), rgba(255,255,255,.015))",
    border: "1px solid rgba(143,163,176,.2)",
    borderTop: "1px solid rgba(215,225,232,.34)",
    transformStyle: "preserve-3d",
  },
  rack: {
    position: "absolute", left: "-3%", right: "-3%", bottom: -9, height: 7,
    background: `linear-gradient(180deg, rgba(215,225,232,.5), transparent 60%),
                 repeating-linear-gradient(90deg, rgba(215,225,232,.38) 0 1px, transparent 1px 8px)`,
    borderRadius: 1, boxShadow: "0 5px 12px rgba(0,0,0,.55)",
  },
  tag: {
    flex: "0 0 auto", width: "clamp(34px,6vw,50px)",
    fontFamily: mono, fontWeight: 700,
    fontSize: "clamp(9px,1.6vw,11px)", letterSpacing: ".1em",
    borderLeft: `2px solid ${C.red}`, paddingLeft: 9, transition: "color .25s",
  },
  shelfBody: { minWidth: 0 },
  shelfH: {
    fontFamily: arch, fontWeight: 800,
    fontSize: "clamp(16px,3.4vw,27px)", lineHeight: 1.02,
    letterSpacing: "-.015em", color: "#fff", textTransform: "uppercase",
  },
  shelfP: {
    fontSize: "clamp(11px,1.9vw,13.5px)", lineHeight: 1.45,
    color: C.steel, marginTop: 5, maxWidth: "34ch",
  },
  go: { marginLeft: "auto", flex: "0 0 auto", fontFamily: mono, fontSize: "clamp(13px,2.2vw,17px)" },

  // --- crisper drawer ------------------------------------------------------
  crisper: {
    marginTop: "auto", position: "relative",
    borderRadius: "5px 5px 4px 4px", overflow: "hidden",
    background: "linear-gradient(180deg, rgba(215,225,232,.2), rgba(215,225,232,.06))",
    boxShadow: `inset 0 1px 0 rgba(215,225,232,.5),
                inset 0 -1px 0 rgba(0,0,0,.35),
                0 -4px 18px rgba(0,0,0,.45)`,
    transformStyle: "preserve-3d",
  },
  grip: {
    height: 7,
    background: "linear-gradient(180deg, rgba(0,0,0,.42), rgba(255,255,255,.14))",
    boxShadow: "inset 0 1px 2px rgba(0,0,0,.6)",
  },
  crisperRow: { display: "flex", gap: 1 },
  crispItem: {
    flex: 1, display: "flex", alignItems: "center", gap: 8,
    padding: "clamp(11px,2.1vw,15px) clamp(11px,2vw,16px)",
    background: "rgba(11,22,34,.42)", transition: "background .25s",
    minWidth: 0,
  },
  crispLabel: {
    fontFamily: mono, fontSize: "clamp(8.5px,1.5vw,10px)",
    letterSpacing: ".16em", textTransform: "uppercase",
    color: C.steelLt, whiteSpace: "nowrap",
  },
  crispMeta: {
    fontFamily: mono, fontSize: "clamp(7.5px,1.3vw,9px)",
    letterSpacing: ".1em", color: C.mute, whiteSpace: "nowrap",
  },
  crispGo: {
    marginLeft: "auto", fontFamily: mono, fontSize: 12,
    color: C.steel, transition: "color .25s, transform .25s",
  },

  // --- modal ---------------------------------------------------------------
  mWrap: {
    position: "fixed", inset: 0, zIndex: 100,
    display: "grid", placeItems: "center",
    padding: "clamp(16px,5vw,40px)",
    background: "rgba(6,14,22,.62)",
    backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
  },
  mCard: {
    position: "relative", width: "min(92vw, 420px)",
    borderRadius: 22,
    background: "linear-gradient(180deg, #FFFFFF 0%, #F4F9FC 100%)",
    border: "1px solid rgba(255,255,255,.7)",
    padding: "clamp(30px,5vw,40px) clamp(26px,4.5vw,36px) clamp(26px,4.5vw,32px)",
    boxShadow: `0 1px 0 rgba(255,255,255,.9) inset,
                0 30px 70px -24px rgba(4,12,20,.65),
                0 2px 10px rgba(4,12,20,.2)`,
    textAlign: "left",
  },
  mX: {
    position: "absolute", top: 14, right: 14,
    width: 32, height: 32, borderRadius: 999,
    display: "grid", placeItems: "center",
    border: "none", background: "transparent",
    color: "#9BB0BE", fontSize: 22, lineHeight: 1,
  },
  mKicker: {
    fontFamily: mono, fontSize: 10, letterSpacing: ".22em",
    textTransform: "uppercase", color: C.red, marginBottom: 14,
  },
  mTitle: {
    fontFamily: arch, fontWeight: 800,
    fontSize: "clamp(24px,5.5vw,32px)", lineHeight: 1.05,
    letterSpacing: "-.02em", color: C.ink, marginBottom: 12,
  },
  mCopy: {
    fontSize: "clamp(13.5px,2.4vw,15px)", lineHeight: 1.6,
    color: C.inkSoft, maxWidth: "34ch", marginBottom: 28,
  },
  mCta: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    minHeight: 46, padding: "0 26px", borderRadius: 999,
    background: C.red, color: "#fff", border: "none",
    fontFamily: mono, fontSize: 10, letterSpacing: ".18em",
    textTransform: "uppercase",
    boxShadow: "0 10px 24px -10px rgba(231,0,26,.75)",
  },

  // small marker on a shelf that is not live yet
  frosted: {
    position: "absolute", top: 8, right: 10,
    fontFamily: mono, fontSize: 8, letterSpacing: ".16em",
    textTransform: "uppercase", color: C.led,
    background: "rgba(143,227,245,.12)",
    border: "1px solid rgba(143,227,245,.3)",
    borderRadius: 999, padding: "3px 8px",
  },

  fog: {
    position: "absolute", left: "-8%", right: "-8%", bottom: "-6%", height: "40%",
    pointerEvents: "none", zIndex: 3,
  },

  // --- doors ---------------------------------------------------------------
  door: {
    position: "absolute", top: 0, bottom: 0, width: "50%", zIndex: 10,
    transformStyle: "preserve-3d",
    // keep the door on its own composited layer for the whole swing, so the
    // browser does not throw the raster away and repaint mid-rotation
    willChange: "transform",
  },
  doorL: { left: 0, transformOrigin: "left center" },
  doorR: { right: 0, transformOrigin: "right center" },

  // The two faces are pushed 1px apart on z. Coplanar at z:0 they z-fight, and
  // as the door swings back through edge-on the renderer can cull both for a
  // frame or two - which shows as a black flash of the cabinet cavity behind.
  face: {
    position: "absolute", inset: 0,
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    transformStyle: "flat",
    display: "flex", flexDirection: "column",
  },
  faceOut: {
    transform: "translateZ(1px)",
    background:
      "linear-gradient(96deg,#E8EEF2 0%,#FBFDFE 17%,#DCE5EB 47%,#F4F8FA 74%,#CFDAE2 100%)",
    boxShadow: `inset 0 0 0 1px rgba(255,255,255,.85),
                inset 0 0 0 6px #E4EBF0,
                inset 0 0 0 7px #B9C7D1,
                0 26px 60px rgba(0,0,0,.5)`,
    padding: "clamp(16px,3vw,28px)",
    alignItems: "flex-start",
    overflow: "hidden",
  },
  // extra right padding keeps the wordmark clear of the centre seam
  faceOutL: { borderRadius: "16px 2px 2px 16px", paddingRight: "clamp(22px,5vw,42px)" },
  faceOutR: { borderRadius: "2px 16px 16px 2px", alignItems: "flex-end", textAlign: "right" },

  grain: {
    position: "absolute", inset: 7, borderRadius: 10,
    background: "repeating-linear-gradient(93deg, rgba(255,255,255,.5) 0 1px, rgba(150,170,185,.11) 1px 3px)",
    opacity: 0.5, pointerEvents: "none",
  },
  // Animated via transform, not `left`. Animating `left` forces a layout and
  // repaint of the door layer on every frame of the sweep, which is what makes
  // a mid-swing raster drop (and the black flash) likely.
  sheen: {
    position: "absolute", top: 0, bottom: 0, left: 0, width: "34%",
    background: "linear-gradient(100deg, transparent, rgba(255,255,255,.72), transparent)",
    transform: "translateX(-120%) skewX(-14deg)",
    pointerEvents: "none",
  },

  brand: { position: "relative", zIndex: 2 },
  brandH: {
    fontFamily: arch, fontWeight: 800,
    fontSize: "clamp(22px,4.6vw,38px)", lineHeight: 0.9,
    letterSpacing: "-.028em", color: C.red, textTransform: "uppercase",
  },
  brandEm: {
    display: "block", fontStyle: "normal", fontWeight: 600,
    fontSize: "clamp(8px,1.5vw,12px)", letterSpacing: ".14em",
    color: C.navy, marginTop: 6, whiteSpace: "nowrap",
  },
  est: {
    position: "relative", zIndex: 2, marginTop: 12,
    fontFamily: mono, fontSize: "clamp(8px,1.5vw,11px)",
    letterSpacing: ".15em", textTransform: "uppercase", color: "#7C8FA0",
  },

  certWrap: { position: "relative", zIndex: 2 },
  cert: {
    fontFamily: mono, fontSize: "clamp(8px,1.5vw,11px)",
    letterSpacing: ".18em", textTransform: "uppercase",
    color: "#7C8FA0", lineHeight: 1.9,
  },

  panel: {
    position: "relative", zIndex: 2, marginTop: "auto", width: "100%",
    background: "linear-gradient(180deg,#14222E,#0A141C)",
    borderRadius: 5,
    padding: "clamp(10px,2vw,16px) clamp(11px,2.2vw,18px)",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 10, textAlign: "left",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,.14), 0 5px 16px rgba(0,0,0,.34)",
  },
  temp: {
    fontFamily: mono, fontWeight: 700,
    fontSize: "clamp(18px,3.6vw,30px)", color: C.led,
    textShadow: "0 0 16px rgba(143,227,245,.6)", letterSpacing: "-.02em",
    fontVariantNumeric: "tabular-nums",
  },
  tempS: { fontSize: ".45em", marginLeft: 3, opacity: 0.7 },
  status: { textAlign: "right" },
  statusP: {
    fontFamily: mono, fontSize: "clamp(7px,1.2vw,9px)",
    letterSpacing: ".18em", textTransform: "uppercase",
    color: "#5F7688", lineHeight: 1.7,
  },
  dot: {
    display: "inline-block", width: 5, height: 5, borderRadius: "50%",
    background: "#41D98A", boxShadow: "0 0 8px #41D98A", marginRight: 5,
  },

  faceIn: {
    transform: "rotateY(180deg) translateZ(1px)", borderRadius: 10,
    background: "linear-gradient(180deg,#E9F0F4,#C7D5DE)",
    boxShadow: "inset 0 0 0 7px #DCE5EB, inset 0 0 0 8px #A9BAC6",
    padding: "clamp(14px,2.8vw,24px)",
    justifyContent: "flex-end", gap: 10,
  },
  gasket: { position: "absolute", inset: 8, borderRadius: 8, border: "4px solid rgba(60,80,95,.22)" },
  bin: {
    height: "14%", borderRadius: 3,
    background: "linear-gradient(180deg, rgba(255,255,255,.6), rgba(160,180,195,.28))",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,.85), 0 2px 5px rgba(0,0,0,.11)",
  },

  handle: {
    position: "absolute", top: "26%", bottom: "26%", width: "var(--bk-handle-w)",
    zIndex: 3,
    // proud of the outer face rather than coplanar with it
    transform: "translateZ(3px)",
    background: "linear-gradient(90deg,#96A8B5,#E7EEF3 38%,#F7FAFC 52%,#A9BAC6 100%)",
    borderRadius: 4,
    boxShadow: "0 4px 14px rgba(0,0,0,.42), inset 0 0 0 1px rgba(255,255,255,.5)",
  },
  handleL: { right: 6 },
  handleR: { left: 6 },
  handleTab: {
    position: "absolute", width: "var(--bk-handle-w)", height: 10,
    background: "linear-gradient(90deg,#8DA0AE,#DCE6EC)", borderRadius: 2,
  },
  tabL: { right: "calc(var(--bk-handle-w) * -0.85)" },
  tabR: { left: "calc(var(--bk-handle-w) * -0.85)" },

  hinge: {
    position: "absolute",
    width: "var(--bk-hinge-w)", height: "var(--bk-hinge-h)", zIndex: 11,
    background: "linear-gradient(90deg,#7E92A1,#C9D6DE 45%,#8296A4)",
    borderRadius: 3, boxShadow: "0 2px 7px rgba(0,0,0,.45)",
  },
  hingeL: { left: "calc(var(--bk-hinge-w) * -0.55)" },
  hingeR: { right: "calc(var(--bk-hinge-w) * -0.55)" },

  seal: {
    position: "absolute", inset: -1, borderRadius: 17, zIndex: 9,
    pointerEvents: "none",
    boxShadow: "0 0 22px 2px rgba(143,227,245,.22)",
  },

  prompt: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    flex: "0 0 auto",
    gap: 10, whiteSpace: "nowrap",
    fontFamily: mono, fontSize: 10, letterSpacing: ".2em",
    textTransform: "uppercase", color: C.steelLt,
    // 48px tall so it is a comfortable tap target on a phone
    minHeight: 48, padding: "0 22px", borderRadius: 999,
    border: "1px solid rgba(143,163,176,.34)",
    background: "rgba(11,22,34,.72)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    boxShadow: "0 8px 24px rgba(0,0,0,.45)",
    zIndex: 30,
  },
    promptI: { display: "block", width: 18, height: 1, background: "currentColor", opacity: 0.45 },

  scrollCue: {
    position: "absolute", left: "50%", bottom: 18,
    transform: "translateX(-50%)",
    display: "flex", flexDirection: "column",
    alignItems: "center", gap: 8,
    pointerEvents: "none", zIndex: 20,
  },
  scrollCueText: {
    fontFamily: mono, fontSize: 9, letterSpacing: ".22em",
    textTransform: "uppercase", color: C.mute,
  },
  scrollCueLine: {
    display: "block", width: 1, height: 26,
    background: `linear-gradient(180deg, ${C.mute}, transparent)`,
  },

  foot: {
    position: "relative", zIndex: 5,
    borderTop: "1px solid rgba(143,163,176,.15)",
    padding: "26px clamp(16px,4vw,44px) 40px",
    display: "flex", flexWrap: "wrap", gap: 18,
    alignItems: "center", justifyContent: "space-between",
    fontSize: 12, color: C.mute,
  },
  footSep: { display: "flex", gap: 20, flexWrap: "wrap" },
  footA: {
    color: C.steelLt, background: "transparent", border: "none",
    padding: 0, fontSize: 12, fontFamily: "inherit",
    borderBottom: "1px solid transparent",
  },
};